import mapValues from 'lodash/mapValues';
import { Trash2 } from 'lucide-react';
import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { toast } from 'sonner';

import {
    ChainlitContext,
    chatSettingsValueState,
    useChatData,
    useChatInteract
} from '@chainlit/react-client';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Translator } from 'components/i18n';

import { chatSettingsOpenState } from 'state/project';

import { FormInput, TFormInputValue } from './FormInput';

export default function ChatSettingsModal() {
    const { chatSettingsValue, chatSettingsInputs, chatSettingsDefaultValue } =
        useChatData();

    const { updateChatSettings } = useChatInteract();
    const [chatSettingsOpen, setChatSettingsOpen] = useRecoilState(
        chatSettingsOpenState
    );
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const chainlitApiClient = useContext(ChainlitContext);

    const { handleSubmit, setValue, reset, watch } = useForm({
        defaultValues: chatSettingsValue
    });
    const setChatSettingsValue = useSetRecoilState(chatSettingsValueState);

    // Reset form when default values change
    useEffect(() => {
        reset(chatSettingsValue);
    }, [chatSettingsValue, reset]);

    const handleClose = (open: boolean) => {
        if (!open) {
            reset(chatSettingsValue);
            setChatSettingsOpen(false);
        }
    };

    const handleConfirm = handleSubmit((data) => {
        const processedValues = mapValues(data, (x: TFormInputValue) =>
            x !== '' ? x : null
        );
        updateChatSettings(processedValues);
        setChatSettingsValue(processedValues);
        setChatSettingsOpen(false);
    });

    const handleReset = () => {
        reset(chatSettingsDefaultValue);
    };

    const handleDeleteAllThreads = async () => {
        setIsDeleting(true);
        toast.loading('Deleting chat history...');

        try {
            const response = await fetch(
                `${window.location.origin}/project/threads/delete-all`,
                {
                    method: 'DELETE',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) {
                throw new Error('Failed to delete threads');
            }

            toast.dismiss();
            toast.success('Chat history deleted successfully');
            setDeleteDialogOpen(false);
            setChatSettingsOpen(false);

            // Reload the page to reflect changes
            setTimeout(() => {
                window.location.href = '/';
            }, 500);
        } catch (error) {
            toast.dismiss();
            toast.error('Failed to delete chat history');
            console.error('Error deleting threads:', error);
        } finally {
            setIsDeleting(false);
        }
    };

    // Legacy setField compatibility layer
    const handleChange = () => {};

    const setFieldValue = (field: string, value: any) => {
        setValue(field, value);
    };

    const values = watch();

    return (
        <>
            <Dialog open={chatSettingsOpen} onOpenChange={handleClose}>
                <DialogContent
                    id="chat-settings"
                    className="flex flex-col gap-6 p-6 min-w-[20vw] max-h-[85vh]"
                >
                    <DialogHeader>
                        <DialogTitle>
                            <Translator path="chat.settings.title" />
                        </DialogTitle>
                        <DialogDescription className="sr-only">
                            <Translator path="chat.settings.customize" />
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex flex-col grow gap-6 overflow-y-auto">
                        {chatSettingsInputs.map((input: any) => (
                            <FormInput
                                key={input.id}
                                element={{
                                    ...input,
                                    value: values[input.id],
                                    onChange: handleChange,
                                    setField: setFieldValue
                                }}
                            />
                        ))}
                        <Separator />
                        <div className="flex justify-between items-center gap-4">
                            <span className="font-normal text-md transition-colors">
                                Delete chat history
                            </span>
                            <Button
                                variant="destructive"
                                onClick={() => setDeleteDialogOpen(true)}
                            >
                                <Trash2 className="mr-2" />
                                <Translator path="chat.settings.actions.delete.button" />
                            </Button>
                        </div>
                    </div>

                    {/* <DialogFooter>
          <Button variant="outline" onClick={handleReset}>
            <Translator path="common.actions.reset" />
          </Button>
          <div className="flex-1" />
          <Button variant="ghost" onClick={() => handleClose(false)}>
            <Translator path="common.actions.cancel" />
          </Button>
          <Button onClick={handleConfirm} id="confirm" autoFocus>
            <Translator path="common.actions.confirm" />
          </Button>
        </DialogFooter> */}
                </DialogContent>
            </Dialog>

            <AlertDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            <Translator path="chat.settings.actions.delete.title" />
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            <Translator path="chat.settings.actions.delete.description" />
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={isDeleting}>
                            <Translator path="common.actions.cancel" />
                        </AlertDialogCancel>
                        <AlertDialogAction variant="destructive" onClick={handleDeleteAllThreads} disabled={isDeleting}>
                                {isDeleting ? (
                                    <Translator path="chat.settings.actions.delete.inProgress" />
                                ) : (
                                    <Translator path="common.actions.confirm" />
                                )}
                            </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
